// migrated 2026-04-27 to SDK realtime — `workspaceProject.realtime.notifications`
// dispatches through the SDK's pluggable provider. The transport is the SDK
// /user-socket registry (server emits `notification-created` to the
// recipient's `user:<id>` room), selected by
// `sdkRealtimeProvider._openNotificationsSocket` and installed in bootShell.
// The Supabase `postgres_changes` provider that used to serve this op was
// removed with the governance project — swapping transports again is a
// provider-only change; this file doesn't move.
// Bare shared-library import, not a deep sdk.js subpath — see app.js's
// identical fix note (tickets/catalog-fixall.md P0). Deferred-call wrapper,
// not a module-scope destructure — see app.js's circular-init note.
import _wsShared from '@symbo-ls/workspace-shared'
const shellGetSDK = (...args) => _wsShared.functions.getSDK(...args)

// Not-ready retries (see subscribeNotifications), counted on the calling
// element: every 2 s for about a minute;
// after that the shell's route-sync self-heal (app.js _ensureShellRealtime)
// keeps calling it.
const NOT_READY_RETRY_MS = 2000
const NOT_READY_RETRY_MAX = 30

/**
 * Subscribe to realtime notifications for the current user.
 * Call once from app init (workspace/app.js onCreate — see the
 * currentUserEmail-gated boot block near subscribeUserEvents). Pushes new
 * notifications into root.notifs and raises EXACTLY ONE notice per event —
 * a native browser Notification while the tab is hidden, an in-app toast
 * (openNotification, with a chime) while it's in the foreground — so the
 * user is never notified twice for the same row.
 *
 * Returns the unsubscribe handle directly; the previous version stashed a
 * Supabase channel on `root._notifChannel` — keep doing that so existing
 * cleanup paths (`root._notifChannel.unsubscribe?.()`) don't crash, but
 * the value now is the unsub fn (callable as a function OR via .unsubscribe).
 */
export const subscribeNotifications = function () {
  const root = this?.state?.root
  if (!root) return
  const me = root.currentUserEmail
  if (!me) return
  // Prevent double-subscribe.
  if (root._notifChannel) return

  let _unsub = null
  let _cancelled = false

  Promise.resolve(shellGetSDK().getService?.('workspaceProject'))
    .then((wp) => {
      if (_cancelled || !wp?.realtime?.subscribeNotifications) return
      // Subscribe only once the transport can open. Too early, the SDK hands
      // back a silent no-op (no realtime provider yet, or no access token for
      // the user socket) that looks exactly like a live handle, and the
      // `_notifChannel` guard then blocks every later attempt: no row and no
      // toast ever arrived for the rest of the session (persona round 4,
      // marcus on iPhone, dev 2026-10-28; reproduced on a clean boot). Not
      // ready → release the guard and try again shortly.
      const sdk = shellGetSDK()
      const signedIn = typeof sdk?.isAuthenticated !== 'function' || !!sdk.isAuthenticated()
      if (typeof wp._realtimeProvider !== 'function' || !signedIn) {
        if (root._notifChannel === handle) root._notifChannel = null
        this._notifNotReadyRetries = (this._notifNotReadyRetries || 0) + 1
        if (this._notifNotReadyRetries <= NOT_READY_RETRY_MAX) {
          setTimeout(() => {
            try {
              this.call('subscribeNotifications')
            } catch (_) {}
          }, NOT_READY_RETRY_MS)
        }
        return
      }
      this._notifNotReadyRetries = 0
      _unsub = wp.realtime.subscribeNotifications({ userEmail: me }, (_event, payload) => {
        const row = payload?.new
        if (!row) return
        const cur = root.notifs || []
        if (cur.some((n) => n.id === row.id)) return
        const notif = _rowToNotif(row)
        root.update({ notifs: [notif, ...cur] })
        // Visibility split — never double-notify for the SAME event: the
        // native browser Notification covers the backgrounded/hidden-tab
        // case (the user can't see an in-app toast right now), an in-app
        // toast covers the foreground case. `this` here is the calling
        // element (arrow function inherits subscribeNotifications' own
        // `this`, bound by el.call), so el.call resolution + the sound
        // pref/de-dup guards inside openNotification/playNotifySound apply
        // exactly as they do for every other openNotification call site.
        if (typeof document !== 'undefined' && document.hidden) {
          _showBrowserNotification(notif)
        } else if (notif.metadata?.kind !== 'chat_message') {
          // CHAT-NOTIFY-1 (tickets/opus.md) — a `chat_message` bell row is
          // ALWAYS accompanied, in this SAME tab, by chat's own realtime
          // toast (subscribeRealtime.js `_notifyNewMessage`, booted at shell
          // scope alongside this subscription — both fire off the identical
          // message insert). That toast already makes the richer decision
          // (skips itself for the sender, a muted channel, or a channel the
          // viewer is actively looking at — chatUtils.js
          // `isChatChannelInView`) and carries a "jump to conversation"
          // action this generic bell toast doesn't. Showing this one too
          // would stack two toasts for one message every time. The
          // backgrounded-tab branch above is unaffected — it's the ONLY
          // path that produces the desktop/OS notification chat never had.
          try {
            this.call('openNotification', {
              title: notif.title || this.call('polyglot', 'workspace.notification.fallbackTitle'),
              message: notif.body || '',
              icon: 'bell',
              href: notif.href || undefined,
              // Entity routing for the toast click — GlobalNotification hands
              // these to openNotificationTarget so a ticket/event toast opens
              // its feature modal in place. The toast's own `type` key is its
              // VISUAL theme, so the notification's entity type rides
              // `notifType` (both just spread onto the queue row).
              notifType: notif.type || undefined,
              metadata: notif.metadata || undefined,
              sound: true
            })
          } catch (err) {
            console.warn('[subscribeNotifications] toast failed:', err?.message || err)
          }
        }
      })
    })
    .catch((err) => {
      console.warn('[subscribeNotifications] subscribe failed:', err?.message || err)
    })

  // Stash a callable handle that supports both fn() and .unsubscribe()
  // shapes so legacy cleanup paths keep working unchanged.
  const handle = () => {
    _cancelled = true
    if (typeof _unsub === 'function') {
      try {
        _unsub()
      } catch (_) {}
    }
  }
  handle.unsubscribe = handle
  root._notifChannel = handle
}

// Dedupe state for fetchNotifications. This is a GLOBAL data load, and its
// call sites each guarded themselves independently — the shell boot timer,
// IntranetAppHeader.onRender (guarded per ELEMENT, so it re-fires every time
// the header element is rebuilt on an SPA route change) and the inbox page.
// Measured on /studio: six `workspaceProject.notifications list` round trips
// for a single navigation, and nineteen across a cold load — 7s of request
// time spent re-fetching a list that had not changed.
//
// Guarding at the function instead of at each call site means a new caller
// cannot reintroduce the storm.
//
// WS-SWITCH-MEMO-PLANE-1: the dedupe state is SCOPED, not global. This is a
// module-level memo, so it survives a workspace switch untouched — and the list
// it guards is workspace-scoped (`workspaceProject.notifications`). Unscoped, a
// switch inside the 30s window served the PREVIOUS workspace's notifications
// and suppressed the refetch that would have corrected them.
// `resetNotificationsCache` below was written for exactly this and was never
// called from anywhere in the tree — which is the argument for keying rather
// than tearing down: the key cannot be forgotten at a call site that does not
// exist. The scope carries the user too, so signing in as someone else misses
// as well.
let NOTIF_PROMISE = null
let NOTIF_SCOPE = null
let NOTIF_AT = 0
const NOTIF_TTL_MS = 30_000

// Same lookup chain the records dedupe cache uses (moduleDataBridge `_readKey`).
// An unresolved workspace yields a DIFFERENT scope than a resolved one, so the
// boot-time entry is missed once the id lands — a miss, never a stale serve.
const _notifScope = (root, me) =>
  String(me || '') +
  '|' +
  String(root?.workspace?.id ?? root?.workspace?._id ?? root?.jwtClaims?.active_workspace_id ?? '')

/**
 * Fetch initial unread + recent notifications via the SDK.
 *
 * Coalesced: concurrent callers share one in-flight request, and a result
 * newer than NOTIF_TTL_MS is reused rather than refetched. Realtime keeps the
 * list live in between (subscribeNotifications pushes inserts straight into
 * `root.notifs`), so this path only needs to cover mount/boot.
 *
 * Pass `{ force: true }` to bypass the TTL — for an explicit user refresh.
 */
export const fetchNotifications = async function (opts) {
  const root = this?.state?.root
  if (!root) return
  const me = root.currentUserEmail
  if (!me) return
  const force = opts && opts.force === true
  const scope = _notifScope(root, me)
  if (!force && NOTIF_SCOPE === scope) {
    if (NOTIF_PROMISE) return NOTIF_PROMISE
    if (Date.now() - NOTIF_AT < NOTIF_TTL_MS) return
  }
  // A scope change invalidates BOTH halves: an in-flight fetch for the previous
  // workspace must not be handed to this caller, and its `NOTIF_AT` stamp must
  // not keep the new scope inside the TTL window.
  if (NOTIF_SCOPE !== scope) {
    NOTIF_PROMISE = null
    NOTIF_AT = 0
    NOTIF_SCOPE = scope
  }
  const mine = (async () => {
    try {
      const result = await shellGetSDK().execute('workspaceProject.notifications', 'list', {})
      // Memo-safe is not mirror-safe: this write lands AFTER the await, so a
      // switch during the round trip would mirror the previous workspace's rows
      // into the new one's `root.notifs` and then stamp the TTL over it. Re-read
      // the scope at completion and drop a result the switch has orphaned.
      if (_notifScope(root, root.currentUserEmail) !== scope) return
      const data = Array.isArray(result?.data) ? result.data : Array.isArray(result) ? result : []
      // `notifsLoaded`: the centre has answered — Home's inbox signal waits
      // for it (functions/homeSignals.js homeSignalsInbox), so a first paint
      // never says "nothing" over unread rows still on their way.
      root.update({ notifs: data.map(_rowToNotif), notifsLoaded: true })
      NOTIF_AT = Date.now()
    } catch (e) {
      console.error('[fetchNotifications]', e?.message || e)
      // a failed read is an answer too: Home does not wait on it forever
      if (!root.notifsLoaded) root.update({ notifsLoaded: true })
    } finally {
      // Only clear the slot if it is still OURS — a newer scope's fetch may have
      // replaced it while this one was in flight.
      if (NOTIF_PROMISE === mine) NOTIF_PROMISE = null
    }
  })()
  NOTIF_PROMISE = mine
  return NOTIF_PROMISE
}

// Signing out / switching workspace must not serve the previous account's
// notifications from the TTL window. NOTE: the scope key above now enforces
// that on its own — this stayed an exported escape hatch, but it is no longer
// load-bearing, and it never was: it had no caller anywhere in the tree.
// A `function`, never an arrow — the functions contract (`el.call` passes the
// element as `this`; a published record derives the scope that holds the memo
// from it). The arrow form threw `Cannot set properties of undefined` there.
export function resetNotificationsCache() {
  NOTIF_PROMISE = null
  NOTIF_SCOPE = null
  NOTIF_AT = 0
}

const _rowToNotif = (r) => ({
  id: r.id,
  userEmail: r.user_email,
  type: r.type,
  title: r.title,
  body: r.body,
  href: r.href,
  metadata: r.metadata || {},
  read: !!r.read,
  createdAt: r.created_at
})

const _showBrowserNotification = (notif) => {
  if (typeof window === 'undefined') return
  if (Notification.permission !== 'granted') return
  try {
    const n = new Notification(notif.title, {
      body: notif.body || '',
      icon: '/favicon.ico',
      tag: notif.id
    })
    if (notif.href) {
      n.onclick = () => {
        window.focus()
        window.location.hash = ''
        window.location.pathname = notif.href
        n.close()
      }
    }
  } catch (e) {
    /* SW required in some browsers */
  }
}
